// SyncedTranscriptRenderer — the Synced reading face (Spotify-lyrics style):
// video left (playable, never blocked on the transcript), transcript right
// with its own independent scroll, the current line pinned beside the video
// with neighbours fading. .claude/analysis/video-source-view-synced-transcript.md
// is the spec this renders (§1 layout, §2 the FOLLOW/BROWSE/SEEK modes, §6
// accessibility).
//
// THE DESIGN BET: the sync logic (§2) is a pure decider (`syncedTranscript.ts`)
// with zero knowledge of the DOM. This component is the decider's ONLY impure
// shell — it owns the `<video>` element, the `timeupdate`/`scroll` listeners,
// and performs whatever effects `decide()` returns (`element.currentTime = t`,
// `scrollIntoView(...)`). Every interaction (`timeupdate` tick, a manual
// scroll, a line click, the "Jump to now" pill) becomes one `SyncEvent`
// dispatched through `applyEvent`, which is the one place effects actually
// run — the same discipline `PdfPages`' `scrollToPage` idiom already uses,
// one level more disciplined because the state machine itself is provably
// pure and independently unit-tested (`syncedTranscript.test.ts`).
//
// This is the promoted counterpart of the sketch,
// `sketches/source-view/SyncedTranscriptSketch.tsx`. What changed on
// promotion: no `SourceHeader`/`UsedInScent` chrome (the live `SourceView`
// shell already renders the header and the "Used in materials" section
// around every renderer — this component owns only the protagonist slot),
// and `content: VideoContent` replaces the sketch's separate
// `videoSrc`/`captionsSrc`/`lines` props (the real shell hands renderers one
// already-parsed projection, per the SourceContent data seam in `types.ts`).

import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { Mono } from "../Mono/Mono.js";
import { Text } from "../Text/Text.js";
import {
    decide,
    type LineId,
    mkInitialSyncState,
    type SyncEffect,
    type SyncEvent,
    type SyncState,
} from "./syncedTranscript.js";
import type { VideoContent } from "./types.js";
import "./SyncedTranscriptRenderer.css";

export type SyncedTranscriptRendererProps = {
    readonly content: VideoContent;
};

// "3:07" / "1:03:07" — a plain display formatter, not a domain concern.
const formatTimecode = (totalSeconds: number): string => {
    const total = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
};

// §6 "Reduced motion" — read once on mount, kept live via the standard
// matchMedia change listener (a user can toggle the OS setting mid-session).
const usePrefersReducedMotion = (): boolean => {
    const [reduced, setReduced] = useState(
        () =>
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    );
    useEffect(() => {
        const query = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReduced(query.matches);
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, []);
    return reduced;
};

export const SyncedTranscriptRenderer = ({
    content,
}: SyncedTranscriptRendererProps) => {
    const { t } = useLingui();
    const { videoSrc, captionsSrc, lines } = content;
    const prefersReducedMotion = usePrefersReducedMotion();

    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLOListElement>(null);
    const lineRefs = useRef<Map<LineId, HTMLButtonElement>>(new Map());
    // §2.4 the scroll-intent guard: true for the short window a PROGRAMMATIC
    // scroll (our own `scrollIntoView`) is in flight, so the container's
    // native `scroll` listener can tell it apart from a genuine user scroll
    // and not immediately kick Follow back into Browse after every re-pin.
    const programmaticScrollRef = useRef(false);

    const [syncState, setSyncState] = useState<SyncState | null>(() =>
        mkInitialSyncState(lines),
    );
    // Read by `applyEvent` so rapid-fire events (successive `timeupdate`
    // ticks arrive faster than React re-renders) always decide from the
    // latest state rather than a stale closure over the last render's state.
    const syncStateRef = useRef(syncState);
    useEffect(() => {
        syncStateRef.current = syncState;
    }, [syncState]);

    const [focusedLineId, setFocusedLineId] = useState<LineId | null>(
        () => syncState?.currentLineId ?? null,
    );

    const scrollLineIntoView = useCallback(
        (lineId: LineId, behavior: "smooth" | "auto") => {
            const el = lineRefs.current.get(lineId);
            const container = containerRef.current;
            if (!el || !container) return;
            programmaticScrollRef.current = true;
            el.scrollIntoView({
                block: "center",
                behavior: prefersReducedMotion ? "auto" : behavior,
            });
            const clear = () => {
                programmaticScrollRef.current = false;
            };
            if ("onscrollend" in container) {
                container.addEventListener("scrollend", clear, { once: true });
            } else {
                // Fallback for browsers without `scrollend` (§2.4).
                window.setTimeout(clear, 400);
            }
        },
        [prefersReducedMotion],
    );

    const runEffects = useCallback(
        (effects: readonly SyncEffect[]) => {
            for (const effect of effects) {
                if (effect.type === "seek_video_to") {
                    const video = videoRef.current;
                    if (video) video.currentTime = effect.time;
                } else {
                    scrollLineIntoView(effect.lineId, effect.behavior);
                }
            }
        },
        [scrollLineIntoView],
    );

    // The ONE place a `SyncEvent` is fed to the pure decider and its effects
    // performed — every listener below funnels through this.
    const applyEvent = useCallback(
        (event: SyncEvent) => {
            const current = syncStateRef.current;
            if (!current) return;
            const { state: next, effects } = decide(current, event, lines);
            syncStateRef.current = next;
            setSyncState(next);
            runEffects(effects);
        },
        [lines, runEffects],
    );

    // `<video>` → PlayerTimeAdvanced. `timeupdate` fires at browser-chosen
    // frequency (~4-66Hz); the decider's own boundary-crossing guard, not a
    // throttle here, is what keeps re-renders bounded to "once per caption
    // line" (§1/§2.5) — this listener may fire often, `decide` mostly no-ops.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onTimeUpdate = () =>
            applyEvent({
                type: "player_time_advanced",
                time: video.currentTime,
            });
        video.addEventListener("timeupdate", onTimeUpdate);
        return () => video.removeEventListener("timeupdate", onTimeUpdate);
    }, [applyEvent]);

    // Transcript pane scroll → UserScrolledTranscript, UNLESS the scroll was
    // our own programmatic re-pin (the guard above).
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const onScroll = () => {
            if (programmaticScrollRef.current) return;
            applyEvent({ type: "user_scrolled_transcript" });
        };
        container.addEventListener("scroll", onScroll, { passive: true });
        return () => container.removeEventListener("scroll", onScroll);
    }, [applyEvent]);

    // Roving-tabindex (§6): arrow keys move focus WITHOUT seeking ("keyboard
    // Browse") — each line is a native `<button>`, so Enter/Space activation
    // (seeking) is the browser's own default; only the arrow-key roving is
    // this app's to implement.
    const handleListKeyDown = (event: KeyboardEvent<HTMLOListElement>) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        const currentIndex = lines.findIndex((l) => l.id === focusedLineId);
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = Math.min(
            Math.max(currentIndex + delta, 0),
            lines.length - 1,
        );
        const nextLine = lines[nextIndex];
        if (nextLine) {
            setFocusedLineId(nextLine.id);
            lineRefs.current.get(nextLine.id)?.focus();
        }
    };

    const currentIndex = syncState
        ? lines.findIndex((l) => l.id === syncState.currentLineId)
        : -1;

    return (
        <div className="bilda-source-view__video-layout">
            <div className="bilda-source-view__video-pane">
                {/* biome-ignore lint/a11y/useMediaCaption: track below carries captions when supplied; native controls give seek/play/volume for free. */}
                <video
                    ref={videoRef}
                    className="bilda-source-view__video"
                    src={videoSrc}
                    controls
                    preload="metadata"
                >
                    {captionsSrc ? (
                        <track
                            kind="captions"
                            src={captionsSrc}
                            srcLang="en"
                            label={t({
                                message: "English",
                                context: "caption track language",
                            })}
                            default
                        />
                    ) : null}
                </video>
            </div>
            <div className="bilda-source-view__video-transcript-pane">
                {lines.length === 0 ? (
                    // §5 graceful degradation — the video keeps playing regardless.
                    <EmptyState
                        icon={<Icon name="video" size="md" />}
                        title={t`No transcript available for this video`}
                        body={t`Upload a caption file (VTT/SRT) to enable synced reading.`}
                    />
                ) : (
                    <>
                        <ol
                            ref={containerRef}
                            className="bilda-source-view__video-lines"
                            aria-label={t`Transcript lines`}
                            onKeyDown={handleListKeyDown}
                        >
                            {lines.map((line, index) => {
                                const isCurrent =
                                    syncState?.currentLineId === line.id;
                                const distance =
                                    currentIndex === -1
                                        ? 3
                                        : Math.min(
                                              Math.abs(index - currentIndex),
                                              3,
                                          );
                                return (
                                    <li key={line.id}>
                                        <button
                                            type="button"
                                            ref={(el) => {
                                                if (el)
                                                    lineRefs.current.set(
                                                        line.id,
                                                        el,
                                                    );
                                                else
                                                    lineRefs.current.delete(
                                                        line.id,
                                                    );
                                            }}
                                            aria-current={
                                                isCurrent ? "true" : undefined
                                            }
                                            tabIndex={
                                                line.id === focusedLineId
                                                    ? 0
                                                    : -1
                                            }
                                            data-distance={distance}
                                            className={`bilda-source-view__video-line${isCurrent ? " bilda-source-view__video-line--current" : ""}`}
                                            onClick={() =>
                                                applyEvent({
                                                    type: "line_clicked",
                                                    lineId: line.id,
                                                })
                                            }
                                            onFocus={() =>
                                                setFocusedLineId(line.id)
                                            }
                                        >
                                            <Mono
                                                tone="dim"
                                                className="bilda-source-view__video-line-time"
                                            >
                                                {formatTimecode(line.start)}
                                            </Mono>
                                            <Text
                                                as="span"
                                                size="sm"
                                                className="bilda-source-view__video-line-text"
                                            >
                                                {line.text}
                                            </Text>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                        {syncState?.type === "browse" ? (
                            <button
                                type="button"
                                className="bilda-source-view__video-resync"
                                onClick={() =>
                                    applyEvent({ type: "resync_requested" })
                                }
                            >
                                <Icon name="dot" size="xs" />
                                {t`Jump to now`}
                            </button>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};
